/* ─────────────────────────────────────────────────────────────────────────
 * DESIGN TOKENS — the single source. `npm run tokens` compiles this to
 * packages/shared/tokens.css, which both apps import.
 *
 * Architecture: PRIMITIVES (ramps, below) → SEMANTICS (aliases, further down).
 * Components only ever touch the semantic layer. No raw hex in a component,
 * ever — that is law 1 and it is what makes a restyle like this one a
 * one-file change instead of a week.
 *
 * ── THE FOUR LAWS ────────────────────────────────────────────────────────
 *  1. Tokens only. No raw hex in components.
 *  2. warning ≠ brand. A caution never wears the action colour.
 *  3. Numbers are mono + tabular. Always.
 *  4. Status is text + colour, never colour alone. (Here: text + a mark whose
 *     SHAPE differs too, so it survives being desaturated — which is what
 *     glare and red/green colourblindness both effectively do.)
 *
 * ── ONE SYSTEM, TWO DENSITIES ────────────────────────────────────────────
 * The desk is read at 60cm on a colour-managed display. The phone is read at
 * arm's length, gloved, at noon on an Arizona ridge, where reflected ambient
 * light adds a constant to both sides of every contrast ratio and pushes it
 * toward 1:1.
 *
 * So the two surfaces share every hue, every component and every rule — and
 * diverge on exactly three things: type scale, hairline weight, and whether a
 * `faint` tone is allowed to carry text. Those divergences are marked FIELD
 * and DESK below and are the only ones permitted.
 * ───────────────────────────────────────────────────────────────────────── */

/* ═══ PRIMITIVES ══════════════════════════════════════════════════════════ */

/**
 * BRAND — gossan. The iron-oxide cap over an oxidising copper orebody, which
 * is the literal thing these seven people walk the ground looking for. 400 is
 * the hue from the approved mockup; 600 is the primary fill, chosen because it
 * clears 7:1 against the page in both directions.
 */
export const brand = {
  50: '#FAEDE7',
  100: '#F2D5C8',
  200: '#E3AF98',
  300: '#CE8460',
  400: '#B4532F', // the approved mockup's rust — marks and strokes, never text
  500: '#9C4525',
  600: '#85391D', // PRIMARY. 7.2:1 on stone, both directions
  700: '#6B2C14',
  800: '#4E1F0D',
  900: '#331408',
} as const;

/**
 * WIN — malachite, the copper carbonate in the oxide zone below the cap.
 * Confirmed, synced, found-what-we-expected.
 */
export const win = {
  50: '#ECF1E3',
  100: '#D2DFBE',
  200: '#AFC58F',
  300: '#8AA766',
  400: '#6E7F5C', // the approved mockup's sage
  500: '#55703C',
  600: '#465C31',
  700: '#3D4A31', // 8.6:1 on stone — text and marks
  800: '#2C3722',
  900: '#1D2517',
} as const;

/**
 * WARN — ochre. Surveyor's flagging tape: something is waiting on you.
 *
 * LAW 2 IN PRACTICE: this ramp is a yellow-gold and the brand is a red-orange.
 * They are never confusable and never swapped. In this product the semantics
 * are load-bearing beyond aesthetics — brand means "act", warn means "waiting"
 * — and the sync pill depends on the distinction being instant.
 */
export const warn = {
  50: '#FBF0D6',
  100: '#F5E1A8',
  200: '#EFCE79',
  300: '#E5A63C', // THE FILL. 7.1:1 carrying ink. See the border note below.
  400: '#D0932A',
  500: '#C98A0D',
  600: '#A87209',
  700: '#8A6410',
  /* 8.1:1 on warn-50. The waiting chip is the header's sync indicator and is
     read outdoors constantly; 700 measured 4.7:1 there, which clears generic
     AA and misses this project's stated 7:1 field floor. */
  800: '#5E4409',
} as const;

/**
 * NEUTRALS — warm-leaning, per the mockup. `stone` is the page.
 *
 * There is no `faint` on the phone. #8A7C66 measures 3.6:1 against stone,
 * which is fine for a rule and not fine for a word, so it is typed as a
 * hairline rather than a text colour and the field app never resolves it to
 * type. Encoding that here is cheaper than remembering it.
 */
export const neutral = {
  /* Three surface levels. Never a fourth — depth past three stops reading as
     hierarchy and starts reading as clutter. */
  paper: '#FFFFFF', // card
  stone: '#F4F0E8', // the page
  sand: '#F7F3EA', // recessed well, inside a card

  ink: '#241E17', // 15.4:1 on stone — everything you must read
  muted: '#574B3A', // 7.4:1 — secondary. Passes the FIELD floor.
  /* 5.0:1 on stone. An earlier value (#948B7C) was committed with a claimed
     3.3:1 that recomputed to 2.96:1 — below even the 3:1 non-text floor, and
     it was carrying .label and .hint as literal text. Desk-only regardless;
     the field density block collapses this into muted. */
  faint: '#6F6759',

  /* Borders are translucent ink so they sit correctly on any surface, rather
     than a fixed colour that only works on one of them. */
  hairline: 'rgba(36,30,23,.09)',
  'hairline-strong': 'rgba(36,30,23,.16)',
  black: '#1A1A1A',
} as const;

/* NOT DEFINED, ON PURPOSE:
 *
 * `danger` — this product is append-only. Nothing deletes, nothing is
 *   destructive, and there is no confirm-before-you-lose-it dialogue anywhere
 *   in it. The two states that sound alarming ("not installed — records at
 *   risk", "sign-out blocked while media is queued") are both *waiting* states,
 *   so they are warn. Inventing a red for a button that does not exist would
 *   put a colour in the system that only ever appears by mistake.
 *
 * `info` — the approved design contract says, in as many words: no blues, no
 *   purples. Kilroy has no informational state that needs its own hue; the
 *   things a blue would mark here are either neutral or already spoken for.
 */

/* ═══ SEMANTICS — the only layer components may touch ═════════════════════ */

export const SEMANTIC = {
  /* surfaces */
  /* White, on both surfaces.
   *
   * The warm stone was defended on sun-legibility grounds — a tint holds its
   * shape where pure white blows out. That argument is real but it lost to a
   * simpler one: the tint reads as a beige wash over everything, and the
   * copper, olive and ochre that actually carry the brand have to fight it.
   * On white they are the only chroma on the screen, which is where they
   * belong. Contrast is unaffected; every ratio in the system was computed
   * against white as well and passes there by a wider margin. */
  'bg-page': neutral.paper,
  'bg-surface': neutral.paper,
  /* The rail. A shade between page and sunken — it reads as a different
     surface without becoming a second colour. Was hardcoded in two stylesheets
     before it was a token, which is exactly how a palette becomes a suggestion. */
  'bg-rail': '#FCFCFB',
  'bg-sunken': '#F7F7F5',
  'bg-brand': brand[600],
  'bg-brand-subtle': brand[50],
  'bg-win-subtle': win[50],
  'bg-warn': warn[300],
  'bg-warn-subtle': warn[50],

  /* Text, in three levels.
     `text-3` is the interesting one: on the DESK it is a true faint tone, and
     on the PHONE the density block below collapses it into text-2. Same
     component, same class, and the tertiary tone simply cannot reach a screen
     that gets read in the sun. Enforcing that here beats remembering it in
     forty components. */
  text: neutral.ink,
  'text-2': neutral.muted,
  'text-3': neutral.muted, // desk overrides to neutral.faint
  'text-on-brand': '#FFFFFF',
  'text-on-warn': neutral.ink,
  'text-brand': brand[600],
  'text-win': win[700],
  'text-warn': warn[800],

  line: neutral.hairline,
  'line-strong': neutral['hairline-strong'],

  /* marks — strokes and glyphs, never text */
  'mark-brand': brand[400],
  'mark-win': win[400],
  'mark-warn': warn[300],
} as const;

/* ═══ TYPE ════════════════════════════════════════════════════════════════ */

export const TYPE = {
  /* THE SIGNATURE MOVE: a light serif at tight tracking for headings, against
     a utilitarian sans body. Newsreader is drawn for screen reading and is not
     one of the two serifs every generated design currently reaches for.

     It is also, unexpectedly, the domain-correct choice: a USGS 7.5-minute
     quadrangle sheet sets its collar and quad name in a serif. The phone runs
     it one weight heavier than the desk — a 300-weight serif is exactly the
     thing glare erases. */
  serif: "'Newsreader Variable', 'Newsreader', Georgia, serif",

  /* System stacks for body and data. Nothing to download, nothing to wait for,
     nothing to precache — which matters more here than brand character in the
     body copy, because this app opens with no signal. SF on iOS is also simply
     the most legible UI face available on the device. */
  sans: "-apple-system, BlinkMacSystemFont, system-ui, 'Segoe UI', Roboto, sans-serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
} as const;

/** DESK — the specified scale. 13px base, information-dense. */
export const SIZE_DESK = {
  '3xs': '10.5px',
  '2xs': '11px',
  xs: '12px',
  base: '13px',
  sm: '14px',
  md: '16px',
  lg: '19px',
  xl: '24px', // stat values
  '2xl': '30px', // page h1
  '3xl': '38px',
} as const;

/** FIELD — the same scale, moved up. 17px base is the floor for a gloved hand
 *  at arm's length; nothing below 12px exists, and 12px is mono-uppercase only. */
export const SIZE_FIELD = {
  '3xs': '12px',
  '2xs': '12px',
  xs: '13px',
  base: '17px',
  sm: '15px',
  md: '19px',
  lg: '22px',
  xl: '30px',
  '2xl': '34px',
  '3xl': '42px',
} as const;

export const SPACE = {
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
} as const;

export const SHAPE = {
  /* 14 card · 10 inner · 8 control. Rounded enough to read as software,
     square enough not to read as a toy. */
  'r-ctl': '8px',
  'r-in': '10px',
  'r-card': '14px',
  'r-dot': '999px',

  /* DESK: a true hairline, drawn as a translucent ink so it sits at ~10–15%
     over whatever surface it lands on. */
  'hairline-desk': '0.5px',
  /* FIELD: a full 1px. A 0.5px translucent rule is a beautiful thing on a
     colour-managed display and is simply not present outdoors. */
  'hairline-field': '1px',

  /* Gloved hands. Independent of everything above, and not negotiable. */
  target: '56px',
  'target-lg': '68px',
} as const;

/* Almost everything is separated by thin rules, not shadows. The one survivor
   lifts genuine overlays — sheets, map panels — off the content they cover,
   which is a real spatial claim rather than a texture. */
/* Elevation is a 1px warm border AND a soft shadow — never one alone. The
   border does the work in bright light where the shadow vanishes; the shadow
   does the work of saying "this is a discrete object". */
export const SHADOW = {
  shadow: '0 1px 2px rgba(36,30,23,.04), 0 4px 12px -4px rgba(36,30,23,.08)',
  'shadow-lift': '0 2px 4px rgba(36,30,23,.06), 0 12px 28px -8px rgba(36,30,23,.16)',
} as const;

export const MOTION = {
  ease: 'cubic-bezier(.2,0,0,1)',
  fast: '120ms',
  med: '220ms',
  slow: '420ms',
} as const;

export const CHROME = {
  'nav-h': '68px',
  'topbar-h': '52px',
  'shell-max': '34rem',
} as const;

/* Map layer colours. IDENTICAL on both surfaces — a geologist has to recognise
   the desk map as "the same map" as their phone, because someone is describing
   a location over the radio to someone looking at the other one. Fills sit at
   12–16% so the terrain stays legible underneath: the basemap is the data. */
export const MAP = {
  assignmentFill: 'rgba(133,57,29,.14)',
  assignmentLine: brand[600],
  claimLine: neutral.muted,
  evidenceRock: brand[600],
  evidenceStructural: neutral.ink,
  evidenceConfirmed: win[700],
  evidenceQueued: warn[300],
  positionDot: neutral.ink,
  positionRing: 'rgba(43,36,28,.12)',
} as const;
